import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';

/**
 * CameraScanner — isolated component for QR scanning.
 *
 * Mounting flow guarantees the target DIV exists BEFORE Html5Qrcode is constructed:
 * 1. Component mounts → div with ref is in DOM.
 * 2. useEffect[] runs ONCE after mount → ref.current is guaranteed non-null.
 * 3. We initialize Html5Qrcode using the actual element id.
 *
 * Robust error handling for common camera issues (permission, no camera, HTTPS, etc).
 */
export default function CameraScanner({ onDecoded, onCancel }) {
  const containerRef = useRef(null);
  const scannerRef = useRef(null);
  const [status, setStatus] = useState('starting'); // starting | ready | error | scanning
  const [error, setError] = useState(null);
  const [scanProgress, setScanProgress] = useState('Menunggu QR Code...');

  useEffect(() => {
    let mounted = true;
    let processingQr = false; // Prevent duplicate processing

    const init = async () => {
      console.log('[CameraScanner] Initializing...');
      // Wait one frame to ensure ref is attached
      await new Promise((r) => requestAnimationFrame(() => r()));
      if (!mounted) return;

      const el = containerRef.current;
      if (!el) {
        console.error('[CameraScanner] Container element not found');
        setError('Elemen kamera tidak ditemukan');
        setStatus('error');
        return;
      }

      // Ensure unique id
      const elId = el.id || 'qr-camera-' + Date.now();
      if (!el.id) el.id = elId;
      console.log('[CameraScanner] Using element ID:', elId);

      try {
        // Probe camera permissions first
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          console.error('[CameraScanner] MediaDevices API not available');
          setError('Browser ini tidak mendukung akses kamera. Coba gunakan Chrome / Safari terbaru, atau gunakan tab "Token Kelas" sebagai alternatif.');
          setStatus('error');
          return;
        }

        const html5 = new Html5Qrcode(elId, { verbose: false });
        scannerRef.current = html5;

        // Try environment camera with high resolution
        const cameraConfig = {
          facingMode: 'environment',
          // Request higher resolution for better QR detection
          advanced: [
            { zoom: 2.0 },
            { focusMode: 'continuous' }
          ]
        };

        const scanConfig = {
          fps: 30, // Maximum FPS for fastest detection
          qrbox: function(viewfinderWidth, viewfinderHeight) {
            // Make QR box larger for easier scanning
            let minEdge = Math.min(viewfinderWidth, viewfinderHeight);
            let qrboxSize = Math.floor(minEdge * 0.85); // 85% of smaller dimension for larger scan area
            return {
              width: qrboxSize,
              height: qrboxSize
            };
          },
          aspectRatio: 1.0,
          disableFlip: false, // Allow horizontal flip for better detection
          videoConstraints: {
            // Request higher resolution for better detection
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            facingMode: 'environment'
          }
        };

        console.log('[CameraScanner] Starting camera with config:', { cameraConfig, scanConfig });

        // Try with advanced config first, fallback to simple config if fails
        let cameraStarted = false;
        try {
          await html5.start(
            cameraConfig,
            scanConfig,
          (decodedText) => {
            // Prevent duplicate processing
            if (processingQr) {
              console.log('[CameraScanner] Already processing QR, ignoring...');
              return;
            }
            processingQr = true;

            console.log('[CameraScanner] QR detected:', decodedText);

            // Update UI immediately
            if (mounted) {
              setScanProgress('✓ QR Code terdeteksi! Memproses...');
              setStatus('scanning');
            }

            // Call onDecoded immediately without waiting for stop
            onDecoded?.(decodedText);

            // Stop scanner in background
            if (scannerRef.current) {
              html5.stop()
                .then(() => {
                  scannerRef.current = null;
                  console.log('[CameraScanner] Scanner stopped');
                })
                .catch((err) => {
                  console.error('[CameraScanner] Error stopping:', err);
                });
            }
          },
          (errorMessage) => {
            // Optional: log scan errors for debugging (uncomment to see what's happening)
            // console.log('[CameraScanner] Scan frame error:', errorMessage);
          });
          cameraStarted = true;
        } catch (advancedError) {
          console.warn('[CameraScanner] Advanced config failed, trying simple config:', advancedError);
          // Fallback to simple configuration
          await html5.start(
            { facingMode: 'environment' },
            {
              fps: 30,
              qrbox: 250,
              disableFlip: false
            },
            (decodedText) => {
              if (processingQr) {
                console.log('[CameraScanner] Already processing QR, ignoring...');
                return;
              }
              processingQr = true;

              console.log('[CameraScanner] QR detected (fallback):', decodedText);

              // Update UI immediately
              if (mounted) {
                setScanProgress('✓ QR Code terdeteksi! Memproses...');
                setStatus('scanning');
              }

              // Call onDecoded immediately without waiting for stop
              onDecoded?.(decodedText);

              // Stop scanner in background
              if (scannerRef.current) {
                html5.stop()
                  .then(() => {
                    scannerRef.current = null;
                    console.log('[CameraScanner] Scanner stopped');
                  })
                  .catch((err) => {
                    console.error('[CameraScanner] Error stopping:', err);
                  });
              }
            },
            (errorMessage) => {
              // Ignore per-frame errors
            }
          );
          cameraStarted = true;
        }

        if (mounted && cameraStarted) {
          console.log('[CameraScanner] Camera started successfully');
          setStatus('ready');
        }
      } catch (e) {
        if (!mounted) return;
        console.error('[CameraScanner] Error initializing camera:', e);
        const msg = (e?.message || String(e)).toLowerCase();
        let userMsg = 'Tidak dapat mengakses kamera.';
        if (msg.includes('permission') || msg.includes('notallowed')) {
          userMsg = 'Izin kamera ditolak. Mohon izinkan akses kamera lewat ikon kunci di address bar, lalu coba lagi.';
        } else if (msg.includes('notfound') || msg.includes('no camera')) {
          userMsg = 'Perangkat ini tidak memiliki kamera. Silakan gunakan tab "Token Kelas" untuk input manual.';
        } else if (msg.includes('notreadable') || msg.includes('starting video')) {
          userMsg = 'Kamera sedang dipakai aplikasi lain. Tutup aplikasi lain (mis. Zoom/Meet) lalu coba lagi.';
        } else if (msg.includes('overconstrained')) {
          userMsg = 'Spesifikasi kamera tidak terpenuhi. Coba di perangkat lain.';
        } else if (msg.includes('securityerror') || msg.includes('https')) {
          userMsg = 'Akses kamera memerlukan koneksi HTTPS. Hubungi admin IT.';
        }
        console.error('[CameraScanner] User-friendly error:', userMsg);
        setError(userMsg);
        setStatus('error');
      }
    };

    init();

    return () => {
      mounted = false;
      if (scannerRef.current) {
        try {
          scannerRef.current.stop()
            .then(() => scannerRef.current?.clear?.())
            .catch(() => {});
        } catch (_e) { /* noop */ }
        scannerRef.current = null;
      }
    };
  }, [onDecoded]);

  const stopAndCancel = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch (_e) { /* noop */ }
      scannerRef.current = null;
    }
    onCancel?.();
  };

  return (
    <div className="space-y-3">
      {status === 'starting' && (
        <div className="py-8 text-center text-slate-600">
          <Loader2 className="h-6 w-6 mx-auto animate-spin mb-2" />
          <p className="text-sm">Memulai kamera…</p>
          <p className="text-xs text-slate-500 mt-1">Browser akan meminta izin akses kamera.</p>
        </div>
      )}

      {status === 'error' && error && (
        <Alert className="bg-rose-50 border-rose-200">
          <AlertCircle className="h-4 w-4 text-rose-700" />
          <AlertDescription className="text-rose-900 text-sm">{error}</AlertDescription>
        </Alert>
      )}

      {/* IMPORTANT: ref div MUST be rendered (not hidden) for Html5Qrcode to mount video inside */}
      <div
        ref={containerRef}
        id="qr-reader-mount"
        data-testid="jurnal-scan-camera"
        className={status === 'ready' ? 'rounded-lg overflow-hidden border-2 border-[#006837]/20' : (status === 'starting' ? 'opacity-0 h-0' : 'hidden')}
        style={{ minHeight: status === 'ready' ? 320 : 0, maxWidth: '100%' }}
      />

      {status === 'ready' && (
        <div className="space-y-2">
          <p className="text-xs text-slate-500 text-center">Arahkan kamera ke kartu QR di depan kelas. Sistem akan mendeteksi secara otomatis.</p>
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200">
              <div className="h-2 w-2 bg-emerald-500 rounded-full animate-pulse"></div>
              <span className="text-xs font-medium text-emerald-700">{scanProgress}</span>
            </div>
          </div>
        </div>
      )}

      {status === 'scanning' && (
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-200">
            <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />
            <span className="text-sm font-semibold text-blue-700">{scanProgress}</span>
          </div>
        </div>
      )}

      <Button variant="outline" onClick={stopAndCancel} className="w-full" data-testid="jurnal-scan-cancel">
        {status === 'error' ? 'Kembali' : 'Batal'}
      </Button>
    </div>
  );
}
